import { useMemo, useState } from "react";

/**
 * Page size options for the dashboard "Recent transactions" module.
 *
 * The dropdown normally offers 5 / 10 / 15 rows, but those options are
 * moot when the user does not have that many transactions. Instead we
 * only offer the steps the user can actually reach, plus a final option equal
 * to the real transaction count so they can always see everything.
 */

/** The standard, always-offered page sizes (when enough transactions exist). */
const TRANSACTION_PAGE_SIZE_STEPS = [5, 10, 15];

/** Page size selected before the user picks anything. */
const DEFAULT_TRANSACTION_PAGE_SIZE = 10;

const SMALLEST_STEP = TRANSACTION_PAGE_SIZE_STEPS[0];
const LARGEST_STEP =
  TRANSACTION_PAGE_SIZE_STEPS[TRANSACTION_PAGE_SIZE_STEPS.length - 1];

type TransactionPageSizeOptions = {
  options: number[];
  isDisabled: boolean;
};

const getTransactionPageSizeOptions = (
  transactionCount: number
): TransactionPageSizeOptions => {
  const options = TRANSACTION_PAGE_SIZE_STEPS.filter(
    (step) => step <= transactionCount
  );

  // Offer the exact count so the user can always reach every transaction,
  // unless it is already a standard step or beyond the largest step.
  if (
    transactionCount > SMALLEST_STEP &&
    transactionCount < LARGEST_STEP &&
    !options.includes(transactionCount)
  ) {
    options.push(transactionCount);
  }

  // With fewer than `SMALLEST_STEP` transactions there is nothing to choose
  // from, but the dropdown still displays the smallest step.
  if (options.length === 0) {
    options.push(SMALLEST_STEP);
  }

  return { options, isDisabled: options.length <= 1 };
};

const resolveTransactionPageSize = (
  preferredPageSize: number,
  options: number[]
): number => {
  if (options.includes(preferredPageSize)) return preferredPageSize;

  const fallback = options
    .filter((option) => option < preferredPageSize)
    .reduce((largest, option) => Math.max(largest, option), 0);

  return fallback || options[0];
};

const useTransactionPageSize = (transactionCount: number) => {
  const [preferredPageSize, setPreferredPageSize] = useState<number>(
    DEFAULT_TRANSACTION_PAGE_SIZE
  );

  const { options, isDisabled } = useMemo(
    () => getTransactionPageSizeOptions(transactionCount),
    [transactionCount]
  );

  return {
    pageSize: resolveTransactionPageSize(preferredPageSize, options),
    options,
    isDisabled,
    setPreferredPageSize,
  };
};

export default useTransactionPageSize;
